import type { Statement } from '../types';
import { pct } from '../lib/format';

export function OpinionMap({ statements }: { statements: Statement[] }) {
  const plotted = statements.slice(0, 42).map((statement, index) => {
    const x = 42 + (statement.agreeRateB - statement.agreeRateA) * 220 + seededJitter(statement.id, 0) * 34;
    const y = 210 - statement.surprise * 145 - statement.bridgeScore * 55 + seededJitter(statement.id, 1) * 26;
    const r = 5 + statement.bridgeScore * 22 + Math.min(12, (statement.nA + statement.nB) * 0.7);
    return { ...statement, x: clamp(x, 28, 372), y: clamp(y, 28, 240), r, index };
  });

  return (
    <section className="card mapCard">
      <div className="mapHeader">
        <div>
          <span className="eyebrow">Realtime opinion space</span>
          <h2>Agreement map</h2>
        </div>
        <p>Left/right = group tilt. Up = underestimated agreement. Big = bridge payoff.</p>
      </div>
      <svg viewBox="0 0 400 270" role="img" aria-label="Opinion-space map showing bridge statements as circles">
        <defs>
          <linearGradient id="bridgeGradient" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#5eead4" />
            <stop offset="0.5" stopColor="#a78bfa" />
            <stop offset="1" stopColor="#fbbf24" />
          </linearGradient>
          <filter id="softGlow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <rect x="10" y="10" width="380" height="245" rx="24" fill="rgba(255,255,255,.035)" stroke="rgba(255,255,255,.12)" />
        <line x1="200" y1="24" x2="200" y2="244" stroke="rgba(255,255,255,.16)" strokeDasharray="4 6" />
        <line x1="24" y1="210" x2="376" y2="210" stroke="rgba(255,255,255,.16)" strokeDasharray="4 6" />
        <text x="30" y="235" className="axisLabel">A tilt</text>
        <text x="322" y="235" className="axisLabel">B tilt</text>
        <text x="224" y="42" className="axisLabel">surprise</text>
        {plotted.map((point) => (
          <g key={point.id} tabIndex={0} className="mapPoint">
            <circle cx={point.x} cy={point.y} r={point.r + 6} fill="url(#bridgeGradient)" opacity={point.bridgeScore > 0.55 ? 0.14 : 0.05} filter="url(#softGlow)" />
            <circle cx={point.x} cy={point.y} r={point.r} fill="url(#bridgeGradient)" opacity={0.35 + point.bridgeScore * 0.58} />
            <title>{`${point.text}\nbridge ${pct(point.bridgeScore)} · surprise ${pct(point.surprise)}`}</title>
          </g>
        ))}
      </svg>
    </section>
  );
}

function seededJitter(id: string, salt: number) {
  let h = 2166136261 + salt;
  for (let i = 0; i < id.length; i += 1) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) / 4294967295 - 0.5) * 2;
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}
